import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, FileDown, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/Layout";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";

export function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <Layout>
      <Seo title={product?.name || "Product"} description={product?.summary || "Digital product details and secure checkout."} />
      <section className="texture-noise px-6 pb-24 pt-36 sm:pt-40">
        <div className="mx-auto max-w-7xl">
          <Button asChild variant="ghost" className="mb-8 rounded-full" data-testid="back-to-shop-button">
            <Link to="/shop"><ArrowLeft className="h-4 w-4" /> Back to shop</Link>
          </Button>
          {loading ? (
            <div data-testid="product-detail-loading" className="rounded-[2rem] bg-white p-10 text-center">Loading product…</div>
          ) : !product ? (
            <div data-testid="product-not-found" className="rounded-[2rem] border border-[#E6E2DA] bg-white p-10 text-center">
              <h1 className="font-heading text-3xl font-extrabold">Product not found</h1>
              <p className="mt-3 text-[#525866]">This product may be unpublished or the link may be incorrect.</p>
            </div>
          ) : (
            <div data-testid="product-detail-page" className="grid gap-10 lg:grid-cols-[1fr_0.85fr]">
              <Reveal>
                <div className="spotlight-frame relative overflow-hidden rounded-[2.5rem] border border-white bg-white shadow-[0_30px_90px_rgba(17,19,21,0.12)]">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} className="aspect-[4/3] w-full object-cover" />
                  ) : (
                    <div className="flex aspect-[4/3] items-center justify-center bg-[#F4F0EA]"><FileDown className="h-20 w-20 text-amber-700/50" /></div>
                  )}
                  <Badge className="absolute left-6 top-6 rounded-full bg-white/90 text-[#111315] backdrop-blur">{product.category}</Badge>
                </div>
              </Reveal>
              <Reveal delay={0.12}>
                <div className="rounded-[2.5rem] border border-[#E6E2DA] bg-white p-7 shadow-sm md:p-10">
                  <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-700">Digital product</p>
                  <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-[-0.04em]">{product.name}</h1>
                  <p className="mt-4 text-lg leading-8 text-[#525866]">{product.summary}</p>
                  <div className="mt-8 flex items-end justify-between border-y border-[#E6E2DA] py-6">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C92A0]">Price</p>
                      <p data-testid="product-price" className="mt-1 font-heading text-4xl font-extrabold">${product.price.toFixed(2)}</p>
                    </div>
                    <p className="text-sm font-semibold text-[#3F5E4D]">{product.currency}</p>
                  </div>
                  <div className="prose prose-neutral mt-6 max-w-none whitespace-pre-line text-sm leading-7 text-[#525866]">{product.description}</div>
                  <div className="mt-8 grid gap-3 sm:grid-cols-2">
                    <div className="flex items-center gap-3 rounded-2xl bg-[#F4F0EA] p-4 text-sm font-semibold"><ShieldCheck className="h-5 w-5 text-[#3F5E4D]" /> PayPal checkout</div>
                    <div className="flex items-center gap-3 rounded-2xl bg-[#F4F0EA] p-4 text-sm font-semibold"><CheckCircle2 className="h-5 w-5 text-[#3F5E4D]" /> Delivery after payment</div>
                  </div>
                  <div className="mt-8">
                    <PayPalCheckout product={product} />
                  </div>
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
